import axios from 'axios';
import { getItem, removeItem, setItem } from '../utils/localStorageControl';
import Cookies from 'js-cookie';
import { notification } from 'antd';

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

const authHeader = () => ({
  Authorization: `Bearer ${getItem('access_token')}`,
});

const client = axios.create({
  baseURL: API_ENDPOINT,
  timeout: 60000, // 60 segundos de timeout
  headers: {
    Authorization: `Bearer ${getItem('access_token')}`,
    'Content-Type': 'application/json',
  },
});

const allClient = axios.create({
  timeout: 60000, // 60 segundos de timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

class DataService {
  static get(path = '') {
    return client({
      method: 'GET',
      url: path,
      headers: { ...authHeader() },
    });
  }

  static getBlob(path = '', timeout?: number) {
    return client({
      method: 'GET',
      url: path,
      headers: { ...authHeader() },
      responseType: 'blob',
      ...(timeout ? { timeout } : {}),
    });
  }

  static postBlob(path = '', data = {}, timeout?: number) {
    return client({
      method: 'POST',
      url: path,
      data,
      headers: { ...authHeader() },
      responseType: 'blob',
      ...(timeout ? { timeout } : {}),
    });
  }

  static getUrl(path = '') {
    return allClient({
      method: 'GET',
      url: path,
      headers: { ...authHeader() },
    });
  }

  static post(path = '', data = {}, optionalHeader = {}, timeout?: number) {
    return client({
      method: 'POST',
      url: path,
      data,
      headers: { ...authHeader(), ...optionalHeader },
      // Per-request timeout override (e.g. long-running AI imports)
      ...(timeout ? { timeout } : {}),
    });
  }

  static patch(path = '', data = {}) {
    return client({
      method: 'PATCH',
      url: path,
      data: JSON.stringify(data),
      headers: { ...authHeader() },
    });
  }

  static patchFormData(path: string, data: FormData) {
    // Sin Content-Type: axios lo deriva del FormData (multipart/form-data; boundary=...).
    // Fijar 'application/json' (el default de client) rompe el parseo multipart de formidable.
    return client({
      method: 'PATCH',
      url: path,
      data,
      headers: { ...authHeader(), 'Content-Type': undefined as any },
    });
  }

  static postFormData(path: string, data: FormData, timeout?: number) {
    return client({
      method: 'POST',
      url: path,
      data,
      headers: { ...authHeader(), 'Content-Type': undefined as any },
      // Per-request timeout override (e.g. long-running AI imports)
      ...(timeout ? { timeout } : {}),
    });
  }

  static putFormData(path: string, data: FormData) {
    // Multipart PUT (mismo tratamiento de Content-Type que patchFormData).
    return client({
      method: 'PUT',
      url: path,
      data,
      headers: { ...authHeader(), 'Content-Type': undefined as any },
    });
  }

  static put(path = '', data = {}) {
    return client({
      method: 'PUT',
      url: path,
      data: JSON.stringify(data),
      headers: { ...authHeader() },
    });
  }

  static delete(path = '') {
    return client({
      method: 'DELETE',
      url: path,
      headers: { ...authHeader() },
    });
  }

  // ── Herramientas: catálogo + tipos ──────────────────────────
  static getTools(filter?: string) {
    return this.get(`/tools${filter ? `?${filter}` : ''}`);
  }

  static getToolById(id: string) {
    return this.get(`/tools/${id}`);
  }

  static createTool(data: any) {
    return this.post('/tools', data);
  }

  static updateTool(id: string, data: any) {
    return this.patch(`/tools/${id}`, data);
  }

  static deleteTool(id: string) {
    return this.delete(`/tools/${id}`);
  }

  static uploadToolImage(id: string, file: File) {
    const formData = new FormData();
    formData.append("image", file);
    return this.postFormData(`/tools/${id}/image`, formData);
  }

  static getToolTypes(filter?: string) {
    return this.get(`/tool-types${filter ? `?${filter}` : ''}`);
  }

  static getToolKits(filter?: string) {
    return this.get(`/tool-kits${filter ? `?${filter}` : ''}`);
  }

  static createToolType(data: any) {
    return this.post('/tool-types', data);
  }

  static updateToolType(id: string, data: any) {
    return this.patch(`/tool-types/${id}`, data);
  }

  static deleteToolType(id: string) {
    return this.delete(`/tool-types/${id}`);
  }

  static getWarehouses() {
    return this.get('/warehouses');
  }

  // ── Herramientas: inventario (solo lectura en este frontend) ──
  static getToolInventories(filter?: string) {
    return this.get(`/tool-inventories${filter ? `?${filter}` : ''}`);
  }

  static getToolInventoryById(id: string) {
    return this.get(`/tool-inventories/${id}`);
  }

  static getToolInventoryLoanHolders(id: string) {
    return this.get(`/tool-inventories/${id}/loan-holders`);
  }

  static getToolInventoryMovements(id: string, filter?: string) {
    return this.get(`/tool-inventories/${id}/movements${filter ? `?${filter}` : ''}`);
  }

  static getToolMovements(filter?: string) {
    return this.get(`/tool-movements${filter ? `?${filter}` : ''}`);
  }

  // ── Herramientas: peticiones de ajuste (ver + aprobar/rechazar;
  //    la creación es exclusiva de la PWA) ────────────────────────
  static getToolAdjustments(filter?: string) {
    return this.get(`/tool-adjustments${filter ? `?${filter}` : ''}`);
  }

  static approveToolAdjustment(id: string) {
    return this.patch(`/tool-adjustments/${id}/approve`, {});
  }

  static rejectToolAdjustment(id: string) {
    return this.patch(`/tool-adjustments/${id}/reject`, {});
  }

  // ── Herramientas: préstamos (solo consulta en este frontend) ──
  static getToolLoans(filter?: string) {
    return this.get(`/tool-loans${filter ? `?${filter}` : ''}`);
  }

  /** Conteo de préstamos con herramientas perdidas (banner). */
  static getToolLoanLossSummary() {
    return this.get(`/tool-loans/loss-summary`);
  }

  // ── RH: descuentos de empleados (pérdida de herramientas etc.) ──
  static getEmployeeDiscounts(filter?: string) {
    return this.get(`/employee-discounts${filter ? `?${filter}` : ''}`);
  }

  static chargeEmployeeDiscount(id: string, note?: string, amount?: number) {
    return this.patch(`/employee-discounts/${id}/charge`, { note, amount });
  }

  static updateEmployeeDiscount(id: string, data: { amount?: number; installments?: number; concept?: string }) {
    return this.patch(`/employee-discounts/${id}`, data);
  }

  static cancelEmployeeDiscount(id: string, reason?: string) {
    return this.patch(`/employee-discounts/${id}/cancel`, { reason });
  }

}

/**
 * axios interceptors runs before and after a request, letting the developer modify req,req more
 * For more details on axios interceptor see https://github.com/axios/axios#interceptors
 */
client.interceptors.request.use(config => {
  // do something before executing the request
  // For example tag along the bearer access token to request header or set a cookie
  let requestConfig: any = config;
  const { headers } = config;
  requestConfig.headers = { ...headers, Authorization: `Bearer ${getItem('access_token')}` };
  return requestConfig;
});

// for multiple requests to be refreshed
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

client.interceptors.response.use(
  (response) => response,
  (error) => {
    /**
     * Do something in case the response returns an error code [3**, 4**, 5**] etc
     * For example, on token expiration retrieve a new access token, retry a failed request etc
     */
    const { response } = error;
    const originalRequest = error.config;

    // Surface the backend's friendly message (ApiError) onto error.message so
    // every component that displays e.message shows something meaningful
    // instead of axios's generic "Request failed with status code 404".
    const apiMessage = response?.data?.message;
    if (typeof apiMessage === 'string' && apiMessage) {
      error.message = apiMessage;
    }

    if (response) {
      if (response.status === 500) {
        console.error('Server error:', response.data);
        return Promise.reject(error);
      } else if (response.status === 401 || response.status === 403) {
        if (response.status === 401 && response.data?.message === 'Please authenticate' && !originalRequest._retry) {
          if (isRefreshing) {
            return new Promise(function (resolve, reject) {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers['Authorization'] = 'Bearer ' + token;
                return client(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          const refreshToken = getItem('refresh_token');

          return axios
            .post(`${API_ENDPOINT}/auth/refresh-tokens`, { refreshToken })
            .then((res) => {
              if (res.status === 200) {
                const { access, refresh } = res.data;
                setItem('access_token', access.token);
                setItem('refresh_token', refresh.token);

                // Update header for the original request
                originalRequest.headers['Authorization'] = 'Bearer ' + access.token;

                processQueue(null, access.token);
                return client(originalRequest);
              }
            })
            .catch((err) => {
              processQueue(err, null);
              Cookies.remove('logedIn');
              removeItem('access_token');
              removeItem('refresh_token');
              removeItem('user');
              notification['error']({
                message: 'Autenticación',
                description: 'Su sesión ha caducado por favor inicie sesión nuevamente',
              });
              window.location.replace(window.location.origin);
              return Promise.reject(err);
            })
            .finally(() => {
              isRefreshing = false;
            });

        }
        return Promise.reject(error);
      } else {
        console.error('HTTP Error:', response.status, response.data);
        return Promise.reject(error);
      }
    }

    // Error de red o timeout
    console.error('Network error:', error.message);
    return Promise.reject(error);
  },
);
export { DataService };
